// File: frontend/src/pages/dashboards/PrincipalDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/axios';

const PrincipalDashboard = () => {
  const { user } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  const [attendanceStats, setAttendanceStats] = useState(null);
  const [activeStaffCount, setActiveStaffCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);

  // Executive AI Operational Insights state
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchAiSummary = async () => {
    try {
      setAiLoading(true);
      const response = await api.get('/api/v1/ai/summary');
      if (response.data?.success && response.data.data) {
        setAiSummary(response.data.data.summary);
      }
    } catch (err) {
      console.error('Error fetching AI summary:', err);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoadingStats(true);
        const [attRes, feeRes, staffRes] = await Promise.all([
          api.get('/api/v1/attendance/summary').catch(() => ({ data: null })),
          api.get('/api/v1/fees/dashboard').catch(() => ({ data: null })),
          api.get('/api/v1/staff', { params: { status: 'active', limit: 1 } }).catch(() => ({ data: null }))
        ]);
        if (attRes.data?.success) setAttendanceStats(attRes.data.data);
        if (staffRes.data?.success) {
          setActiveStaffCount(staffRes.data.pagination?.total || (staffRes.data.data || []).length);
        }
      } catch (error) {
        console.error('Error loading principal dashboard stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const principalName = user?.name || 'Principal';
  const attendanceRate = attendanceStats?.attendance_percentage !== undefined && attendanceStats?.attendance_percentage !== null
    ? attendanceStats.attendance_percentage
    : 0;

  const isSuperAdmin = user?.role === 'super_admin';
  const isAiInsightsUnlocked = isSuperAdmin || (school?.feature_ai_insights !== 0 && school?.feature_ai_agent !== 0);
  const isAiTestGenUnlocked = isSuperAdmin || school?.feature_ai_test_gen !== 0;
  const isTimetableUnlocked = isSuperAdmin || school?.feature_timetable !== 0;

  // Calculate License Expiry Countdown
  const calculateDaysRemaining = () => {
    if (!school?.license_expiry) return null;
    const expiry = new Date(school.license_expiry).getTime();
    const today = new Date().getTime();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };
  const daysRemaining = calculateDaysRemaining();

  return (
    <DashboardLayout>
      {/* Sub-Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Principal Executive Portal</h1>
          <p className="text-xs text-purple-600 font-semibold mt-0.5">
            Dashboard <span className="text-slate-400">/</span> Academic & Staff Supervision
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {isAiTestGenUnlocked ? (
            <button
              onClick={() => navigate('/ai-tests')}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-extrabold rounded-xl shadow-md transition flex items-center space-x-1.5"
            >
              <span>✨</span>
              <span>AI Test Generator</span>
            </button>
          ) : (
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20AI%20Test%20Generator%20Pro%20feature"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-amber-500/20 text-amber-800 hover:bg-amber-500/30 text-xs font-bold rounded-xl border border-amber-500/30 flex items-center space-x-1.5 transition"
              title="Locked Pro Feature - Click to Upgrade via WhatsApp"
            >
              <span>💬</span>
              <span>AI Test Gen (Locked Pro)</span>
            </a>
          )}
          {isTimetableUnlocked ? (
            <button
              onClick={() => navigate('/timetable')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-blue-glow transition flex items-center space-x-1.5"
            >
              <span>🗓️</span>
              <span>Class Timetable</span>
            </button>
          ) : (
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20Class%20Timetable%20Pro%20feature"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500/20 text-blue-800 hover:bg-blue-500/30 text-xs font-bold rounded-xl border border-blue-500/30 flex items-center space-x-1.5 transition"
              title="Locked Pro Feature - Click to Upgrade via WhatsApp"
            >
              <span>💬</span>
              <span>Timetable (Locked Pro)</span>
            </a>
          )}
          <button
            onClick={() => navigate('/reports')}
            className="px-4 py-2 bg-white hover:bg-purple-50 text-slate-700 text-xs font-semibold rounded-xl border border-purple-200 shadow-sm transition"
          >
            📊 Executive Reports
          </button>
        </div>
      </div>

      {/* Hero Card with License Expiry Countdown */}
      <div className="bg-purple-gradient rounded-3xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="px-3 py-1 bg-white/10 text-purple-200 rounded-full text-[10px] font-extrabold uppercase tracking-wider border border-white/10">
              EXECUTIVE DESK
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold mt-2">
              Welcome, Principal {principalName}! 👑
            </h2>
            <p className="text-purple-200 text-xs sm:text-sm mt-1">
              Overseeing academic performance, staff attendance, and school operations.
            </p>
          </div>
          <div className="flex flex-col sm:items-end space-y-2">
            <div className="text-right bg-white/10 px-4 py-1.5 rounded-2xl border border-white/10 text-xs font-bold text-purple-100">
              School Health: <span className="text-emerald-400 font-extrabold">98.4% Excellent</span>
            </div>

            {/* License Expiry Countdown Badge */}
            {school?.license_expiry ? (
              <div className="text-right bg-white/15 px-3.5 py-1.5 rounded-xl border border-white/20 text-xs font-bold flex items-center justify-end space-x-1.5 shadow-sm">
                <span>⏳</span>
                <span>
                  License Expiry:{' '}
                  <span className={daysRemaining !== null && daysRemaining < 30 ? 'text-rose-300 font-extrabold' : 'text-amber-300 font-extrabold'}>
                    {daysRemaining !== null ? `${daysRemaining} Days Remaining` : 'Lifetime'}
                  </span>
                </span>
              </div>
            ) : (
              <div className="text-right bg-white/15 px-3.5 py-1.5 rounded-xl border border-white/20 text-xs font-bold flex items-center justify-end space-x-1.5 shadow-sm">
                <span>🔑</span>
                <span>License: <span className="text-emerald-300 font-extrabold">Lifetime Access</span></span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Executive AI Operational Insights Card (Feature Gated) */}
      {isAiInsightsUnlocked ? (
        <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 shadow-xl border border-indigo-500/20 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div className="flex items-center space-x-3">
              <span className="w-10 h-10 rounded-2xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-xl font-bold border border-indigo-500/30">
                ✨
              </span>
              <div>
                <h2 className="font-extrabold text-base tracking-wide flex items-center gap-2">
                  Executive AI Operational Insights
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full uppercase font-bold">
                    Groq Llama-3.3 Live
                  </span>
                </h2>
                <p className="text-xs text-slate-400">Automated daily attendance, fee revenue & defaulter analysis</p>
              </div>
            </div>

            <button
              onClick={fetchAiSummary}
              disabled={aiLoading}
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {aiLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Analyzing Database...</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>{aiSummary ? 'Re-Analyze Today\'s Data' : 'Generate Daily Insight'}</span>
                </>
              )}
            </button>
          </div>

          {aiSummary ? (
            <div className="bg-slate-950/60 rounded-2xl p-5 border border-white/10 text-xs sm:text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">
              {aiSummary}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic py-2 flex items-center space-x-2">
              <span>💡 Click <strong>"Generate Daily Insight"</strong> to let AI analyze today's MySQL database records (Attendance, Fee Receipts, Overdue Defaulters).</span>
            </div>
          )}
        </div>
      ) : (
        /* Sleek PRO AI Lock Banner for Basic Plan Client Schools */
        <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white rounded-3xl p-6 shadow-xl border border-purple-500/30 space-y-3 relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <span className="w-10 h-10 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-xl font-bold border border-amber-500/30">
                🔒
              </span>
              <div>
                <h2 className="font-extrabold text-base tracking-wide flex items-center gap-2">
                  Executive AI Operational Insights
                  <span className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full uppercase font-bold">
                    PRO AI MODULE
                  </span>
                </h2>
                <p className="text-xs text-slate-400">Automated daily attendance, fee revenue & defaulter analysis powered by Groq Llama-3.3</p>
              </div>
            </div>
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20Executive%20AI%20Operational%20Insights%20module"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center space-x-2 shrink-0"
            >
              <span>💬</span>
              <span>Upgrade via WhatsApp (+92 317 1818108)</span>
            </a>
          </div>
          <div className="p-4 bg-slate-950/60 rounded-2xl border border-white/5 text-xs text-slate-400 flex items-center justify-between">
            <span>💡 Contact software vendor (Waleed Technologies) to activate the Executive AI module for your school portal.</span>
            <span className="font-mono text-[10px] text-amber-400 font-bold uppercase">Enterprise Feature</span>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-purple-600 uppercase">Attendance Rate</span>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Good</span>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900">{attendanceRate}%</h3>
          <p className="text-xs text-slate-500 mt-1">Daily Average Attendance</p>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-purple-600 uppercase">Active Teaching Staff</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Active</span>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900">{loadingStats ? '...' : activeStaffCount}</h3>
          <p className="text-xs text-slate-500 mt-1">Faculty & Personnel</p>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-purple-100 shadow-purple-subtle">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-purple-600 uppercase">Term Examinations</span>
            <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-bold">Scheduled</span>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900">Mid-Term 2024</h3>
          <p className="text-xs text-slate-500 mt-1">Grades 1 through 10</p>
        </div>
      </div>

      {/* Management Quick Actions Grid */}
      <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-purple-subtle space-y-4">
        <h3 className="text-base font-bold text-slate-900 border-b border-purple-100 pb-3">
          Principal Quick Actions
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/students')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-2xl border border-purple-100 text-left transition"
          >
            <div className="text-2xl mb-1">👨‍🎓</div>
            <span className="font-bold text-xs text-purple-950 block">Student Directory</span>
          </button>
          <button
            onClick={() => navigate('/staff')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-2xl border border-purple-100 text-left transition"
          >
            <div className="text-2xl mb-1">👨‍🏫</div>
            <span className="font-bold text-xs text-purple-950 block">Staff Directory</span>
          </button>
          <button
            onClick={() => navigate('/attendance/summary')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-2xl border border-purple-100 text-left transition"
          >
            <div className="text-2xl mb-1">📅</div>
            <span className="font-bold text-xs text-purple-950 block">Attendance Summary</span>
          </button>
          <button
            onClick={() => navigate('/exams')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-2xl border border-purple-100 text-left transition"
          >
            <div className="text-2xl mb-1">📝</div>
            <span className="font-bold text-xs text-purple-950 block">Exams & Grades</span>
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PrincipalDashboard;
